import QRCode from "qrcode";
import { toast } from "sonner";

// Same-origin logo copy so the poster canvas stays exportable (Save / Share).
const LOGO_LOCAL = "/nexto-logo.png";

function hexToRgba(hex, a = 1) {
  let h = (hex || "").replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6) return `rgba(26,26,26,${a})`;
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}

const loadImage = (src, crossOrigin) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = crossOrigin;
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

// Draw a poster onto a high-res canvas and return { dataUrl, width, height }.
function renderPosterCanvas({ qrImg, logoImg, headlineLines, sub, code, host, accent = "#D4AF37", primaryColor = "" }) {
  const W = 1080, H = 1500;
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const ctx = c.getContext("2d");

  // Background — subtle wash of the school's primary color
  ctx.fillStyle = "#0a0a0a";
  ctx.fillRect(0, 0, W, H);
  if (primaryColor) {
    const g = ctx.createRadialGradient(W / 2, -60, 60, W / 2, 260, H * 1.05);
    g.addColorStop(0, hexToRgba(primaryColor, 0.55));
    g.addColorStop(0.45, hexToRgba(primaryColor, 0.18));
    g.addColorStop(1, "rgba(10,10,10,0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
  } else {
    const g = ctx.createRadialGradient(W / 2, 0, 60, W / 2, 300, H);
    g.addColorStop(0, "#1a1a1a");
    g.addColorStop(0.6, "#0a0a0a");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
  }

  ctx.textAlign = "center";
  const cx = W / 2;

  // Logo
  if (logoImg) {
    const ls = 190;
    try { ctx.drawImage(logoImg, cx - ls / 2, 70, ls, ls); } catch (e) {}
  }

  // Kicker
  ctx.fillStyle = accent;
  ctx.font = "800 26px Arial, sans-serif";
  ctx.save();
  ctx.letterSpacing = "8px";
  ctx.fillText("NEXT O SOCCER · ELITE TECHNICAL TRAINING", cx, 320);
  ctx.restore();

  // Headline — auto-fit font size, wrap if still too wide
  let hy = 400;
  const headlineMaxW = 940;
  headlineLines.forEach((ln) => {
    ctx.fillStyle = ln.gold ? accent : "#ffffff";
    const text = ln.text.toUpperCase();
    let size = 76;
    ctx.font = `800 ${size}px Arial, sans-serif`;
    while (size > 44 && ctx.measureText(text).width > headlineMaxW) {
      size -= 4; ctx.font = `800 ${size}px Arial, sans-serif`;
    }
    if (ctx.measureText(text).width > headlineMaxW) {
      const wds = text.split(" ");
      let l = "";
      wds.forEach((wd) => {
        const test = l ? `${l} ${wd}` : wd;
        if (ctx.measureText(test).width > headlineMaxW) { ctx.fillText(l, cx, hy); hy += size * 1.05; l = wd; }
        else { l = test; }
      });
      if (l) { ctx.fillText(l, cx, hy); hy += size * 1.1; }
    } else {
      ctx.fillText(text, cx, hy); hy += size * 1.1;
    }
  });

  // Sub text (wrapped)
  ctx.fillStyle = "#cfcfcf";
  ctx.font = "400 30px Arial, sans-serif";
  const words = sub.split(" ");
  let line = "", sy = hy + 24;
  const maxW = 760;
  words.forEach((wd) => {
    const test = line ? `${line} ${wd}` : wd;
    if (ctx.measureText(test).width > maxW) {
      ctx.fillText(line, cx, sy); line = wd; sy += 42;
    } else { line = test; }
  });
  if (line) { ctx.fillText(line, cx, sy); sy += 42; }

  // QR box
  const qrBoxY = sy + 30;
  const box = 440, pad = 26, qsize = box - pad * 2;
  const boxX = cx - box / 2;
  // accent ring
  ctx.fillStyle = accent;
  roundRect(ctx, boxX - 8, qrBoxY - 8, box + 16, box + 16, 28); ctx.fill();
  ctx.fillStyle = "#ffffff";
  roundRect(ctx, boxX, qrBoxY, box, box, 22); ctx.fill();
  ctx.drawImage(qrImg, boxX + pad, qrBoxY + pad, qsize, qsize);

  // Code line
  let cyy = qrBoxY + box + 60;
  ctx.fillStyle = "#9a9a9a";
  ctx.font = "400 24px Arial, sans-serif";
  ctx.fillText(`or go to ${host}/redeem`, cx, cyy);
  cyy += 46;
  ctx.fillStyle = "#ffffff";
  ctx.font = "700 40px 'Courier New', monospace";
  ctx.fillText(code, cx, cyy);

  // Footer
  ctx.fillStyle = "#777777";
  ctx.font = "600 22px Arial, sans-serif";
  ctx.fillText("POWERED BY NEXT O SOCCER · CREATING OPPORTUNITIES WITH ELITE TRAINING", cx, H - 60);

  return { dataUrl: c.toDataURL("image/png"), width: W, height: H };
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// Opens a branded QR flyer with Close / Save Image / Share / Print controls.
// Pass logoUrl + accent to brand it with an institution's mascot & colors.
export async function openTrialPoster({ code, teamName = "", logoUrl = "", accent = "#D4AF37", primaryColor = "" }) {
  const url = `${window.location.origin}/redeem?code=${encodeURIComponent(code)}`;
  let qrDataUrl;
  try {
    qrDataUrl = await QRCode.toDataURL(url, { width: 620, margin: 1, color: { dark: "#111111", light: "#ffffff" } });
  } catch { toast.error("Couldn't build the QR poster."); return; }

  const qrImg = await loadImage(qrDataUrl).catch(() => null);
  let logoImg = null;
  if (logoUrl) logoImg = await loadImage(logoUrl, "anonymous").catch(() => null);
  if (!logoImg) logoImg = await loadImage(LOGO_LOCAL, "anonymous").catch(() => null);

  const headlineLines = teamName
    ? [{ text: "Join", gold: false }, { text: teamName, gold: true }]
    : [{ text: "Scan For", gold: false }, { text: "7 Days Free", gold: true }, { text: "Elite Coaching", gold: false }];
  const sub = teamName
    ? "Scan to claim a free 7-day Elite trial and join your team's private leaderboard. No card needed."
    : "Unlimited coach video reviews, the monthly roadmap, performance rings & your team leaderboard — free for a week. No card needed.";

  let poster;
  try {
    poster = renderPosterCanvas({ qrImg, logoImg, headlineLines, sub, code, host: window.location.host, accent, primaryColor });
  } catch (e) { toast.error("Couldn't render the poster."); return; }

  const w = window.open("", "_blank");
  if (!w) { toast.error("Allow pop-ups to open the poster."); return; }

  const fileName = `next-o-${(teamName || code).toString().toLowerCase().replace(/[^a-z0-9]+/g, "-")}-flyer.png`;

  w.document.write(`<!doctype html><html><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover"/>
<title>Next O Soccer — QR Flyer</title>
<style>
  * { box-sizing: border-box; }
  body { margin:0; font-family: Arial, Helvetica, sans-serif; background:#0a0a0a; color:#fff; }
  .toolbar { position: sticky; top:0; z-index:10; display:flex; gap:10px; flex-wrap:wrap;
             align-items:center; padding: calc(env(safe-area-inset-top,0px) + 14px) 18px 14px;
             background: rgba(13,13,13,0.85); backdrop-filter: blur(14px);
             -webkit-backdrop-filter: blur(14px); border-bottom:1px solid rgba(255,255,255,0.1); }
  .btn { display:inline-flex; align-items:center; gap:8px; border:1px solid rgba(255,255,255,0.18);
         background: rgba(255,255,255,0.06); color:#fff; font-weight:800; text-transform:uppercase;
         letter-spacing:1px; font-size:12px; padding:11px 16px; border-radius:8px; cursor:pointer;
         transition: all .15s ease; }
  .btn:hover { border-color: rgba(255,255,255,0.45); background: rgba(255,255,255,0.12); }
  .btn.gold { background:#D4AF37; color:#111; border-color:#D4AF37; }
  .btn.gold:hover { background:#e8c65a; }
  .btn.close { margin-right:auto; }
  .btn svg { width:16px; height:16px; }
  .stage { display:flex; justify-content:center; padding: 26px 16px 60px; }
  img.poster { width: 100%; max-width: 520px; height:auto; border-radius:16px;
               box-shadow: 0 20px 60px rgba(0,0,0,0.6); }
  @media print {
    @page { size: portrait; margin: 0; }
    .toolbar { display:none !important; }
    .stage { padding:0; }
    img.poster { max-width:100%; border-radius:0; box-shadow:none; }
    body { background:#fff; }
  }
</style></head><body>
  <div class="toolbar no-print">
    <button class="btn close" onclick="window.close()" data-testid="poster-close">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M15 18l-6-6 6-6"/></svg> Close
    </button>
    <button class="btn gold" id="saveBtn" data-testid="poster-save">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M7 10l5 5 5-5"/><path d="M12 15V3"/></svg> Save Image
    </button>
    <button class="btn" id="shareBtn" data-testid="poster-share">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4"/></svg> Share
    </button>
    <button class="btn" onclick="window.print()" data-testid="poster-print">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M6 9V2h12v7"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><path d="M6 14h12v8H6z"/></svg> Print
    </button>
  </div>
  <div class="stage"><img class="poster" src="${poster.dataUrl}" alt="Next O Soccer QR flyer"/></div>
  <script>
    var DATA_URL = ${JSON.stringify(poster.dataUrl)};
    var FILE_NAME = ${JSON.stringify(fileName)};
    var SHARE_URL = ${JSON.stringify(url)};
    var SHARE_TITLE = ${JSON.stringify(teamName ? "Join " + teamName + " on Next O Soccer" : "Next O Soccer — Free Elite Trial")};
    function dataURLtoBlob(d){ var a=d.split(','), m=a[0].match(/:(.*?);/)[1], b=atob(a[1]), n=b.length, u=new Uint8Array(n); while(n--){u[n]=b.charCodeAt(n);} return new Blob([u],{type:m}); }
    document.getElementById('saveBtn').addEventListener('click', function(){
      var a=document.createElement('a'); a.href=DATA_URL; a.download=FILE_NAME; document.body.appendChild(a); a.click(); a.remove();
    });
    document.getElementById('shareBtn').addEventListener('click', async function(){
      try {
        var blob = dataURLtoBlob(DATA_URL);
        var file = new File([blob], FILE_NAME, { type:'image/png' });
        if (navigator.canShare && navigator.canShare({ files:[file] })) {
          await navigator.share({ files:[file], title: SHARE_TITLE, text: SHARE_TITLE });
          return;
        }
        if (navigator.share) { await navigator.share({ title: SHARE_TITLE, text: SHARE_TITLE, url: SHARE_URL }); return; }
        await navigator.clipboard.writeText(SHARE_URL);
        alert('Share link copied to clipboard:\\n' + SHARE_URL);
      } catch (e) { /* user cancelled */ }
    });
  <\/script>
</body></html>`);
  w.document.close();
}
